import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import TableContainer from "@mui/material/TableContainer";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
} from "@mui/material";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { GET_CONFIG, UPDATE_CONFIG } from "../config/graphql";
import { PageContainer } from "../../components/PageContainer";
import { ButtonContainer } from "../../components/ButtonContainer";
import { ConfigInputType } from "../../__generated__/graphql";

const AREA_CAPACITY_KEY = "AREA_CAPACITY";

type AreaCapacityColor = "red" | "amber" | "green";

type AreaCapacityType = "Area" | "Campsite" | "Stage";

type AreaCapacity = {
  id: string;
  name: string;
  order: number;
  color: AreaCapacityColor;
  type: AreaCapacityType;
  imageUrl?: string;
  capacity?: number;
};

const COLOR_OPTIONS: { value: AreaCapacityColor; label: string; swatch: string }[] = [
  { value: "green", label: "Green", swatch: "#2e7d32" },
  { value: "amber", label: "Amber", swatch: "#ed6c02" },
  { value: "red", label: "Red", swatch: "#d32f2f" },
];

const TYPE_OPTIONS: AreaCapacityType[] = ["Area", "Campsite", "Stage"];

type AreaCapacityThresholds = {
  amber: number;
  red: number;
};

const DEFAULT_THRESHOLDS: AreaCapacityThresholds = { amber: 60, red: 90 };

const swatchFor = (color: AreaCapacityColor): string =>
  COLOR_OPTIONS.find((option) => option.value === color)?.swatch ?? "#2e7d32";

const colorForCapacity = (
  capacity: number,
  thresholds: AreaCapacityThresholds
): AreaCapacityColor => {
  if (capacity >= thresholds.red) return "red";
  if (capacity >= thresholds.amber) return "amber";
  return "green";
};

const normalizeOrder = (value?: number | null): number => {
  if (typeof value !== "number" || Number.isNaN(value)) return 0;
  return Math.max(0, value);
};

const normalizeCapacity = (value: unknown): number | undefined => {
  if (typeof value !== "number" || Number.isNaN(value)) return undefined;
  return Math.min(100, Math.max(0, value));
};

const normalizeImageUrl = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const isColor = (value: unknown): value is AreaCapacityColor =>
  value === "red" || value === "amber" || value === "green";

const isType = (value: unknown): value is AreaCapacityType =>
  value === "Area" || value === "Campsite" || value === "Stage";

const normalizeThresholds = (value: unknown): AreaCapacityThresholds => {
  const record = value as Record<string, unknown> | undefined;
  const amber = normalizeCapacity(record?.amber) ?? DEFAULT_THRESHOLDS.amber;
  const red = normalizeCapacity(record?.red) ?? DEFAULT_THRESHOLDS.red;
  if (amber >= red) return DEFAULT_THRESHOLDS;
  return { amber, red };
};

type AreaCapacityData = {
  areas: AreaCapacity[];
  lastUpdated: string | null;
  thresholds: AreaCapacityThresholds;
};

const parseAreaCapacity = (value: unknown): AreaCapacityData => {
  let raw: unknown = value;
  if (typeof value === "string") {
    if (!value.trim().length) {
      return { areas: [], lastUpdated: null, thresholds: DEFAULT_THRESHOLDS };
    }
    try {
      raw = JSON.parse(value);
    } catch {
      return { areas: [], lastUpdated: null, thresholds: DEFAULT_THRESHOLDS };
    }
  }

  // Older/foreign shapes: a bare array with no lastUpdated wrapper.
  const rawAreas = Array.isArray(raw) ? raw : (raw as Record<string, unknown>)?.areas;
  const rawLastUpdated = Array.isArray(raw)
    ? null
    : (raw as Record<string, unknown>)?.lastUpdated;
  const rawThresholds = Array.isArray(raw)
    ? undefined
    : (raw as Record<string, unknown>)?.thresholds;
  const thresholds = normalizeThresholds(rawThresholds);

  if (!Array.isArray(rawAreas)) return { areas: [], lastUpdated: null, thresholds };

  const areas = rawAreas
    .map((item, index) => {
      const record = item as Record<string, unknown>;
      const capacity = normalizeCapacity(record?.capacity);
      return {
        id: typeof record?.id === "string" ? record.id : String(index),
        name: typeof record?.name === "string" ? record.name : "",
        order: normalizeOrder(record?.order as number | undefined),
        color:
          capacity !== undefined
            ? colorForCapacity(capacity, thresholds)
            : isColor(record?.color)
              ? record.color
              : "green",
        type: isType(record?.type) ? record.type : "Area",
        imageUrl: normalizeImageUrl(record?.imageUrl),
        capacity,
      };
    })
    .sort((a, b) => a.order - b.order);

  return {
    areas,
    lastUpdated: typeof rawLastUpdated === "string" ? rawLastUpdated : null,
    thresholds,
  };
};

const reorderAreas = (
  items: AreaCapacity[],
  draggedAreaId: string,
  targetAreaId: string
): AreaCapacity[] => {
  const fromIndex = items.findIndex((item) => item.id === draggedAreaId);
  const toIndex = items.findIndex((item) => item.id === targetAreaId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return items;
  }

  const reordered = [...items];
  const [draggedItem] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, draggedItem);

  return reordered.map((item, index) => ({ ...item, order: index }));
};

const ColorSwatch = ({ color }: { color: AreaCapacityColor }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: 12,
      height: 12,
      borderRadius: "50%",
      backgroundColor: swatchFor(color),
      mr: 1,
    }}
  />
);

const Areas = () => {
  const { data, loading } = useQuery(GET_CONFIG);
  const [updateConfig, { loading: saving }] = useMutation(UPDATE_CONFIG, {
    awaitRefetchQueries: true,
    refetchQueries: ["config"],
  });

  const savedAreaCapacity = React.useMemo(() => {
    const configRow = data?.configs.find(
      (config: { key: string; value: unknown }) => config.key === AREA_CAPACITY_KEY
    );
    return parseAreaCapacity(configRow?.value);
  }, [data]);
  const savedAreas = savedAreaCapacity.areas;
  const savedThresholds = savedAreaCapacity.thresholds;

  const [areas, setAreas] = React.useState<AreaCapacity[]>([]);
  const [lastUpdated, setLastUpdated] = React.useState<string | null>(null);
  const [thresholds, setThresholds] = React.useState<AreaCapacityThresholds>(DEFAULT_THRESHOLDS);
  React.useEffect(() => {
    setAreas(savedAreaCapacity.areas);
    setLastUpdated(savedAreaCapacity.lastUpdated);
    setThresholds(savedAreaCapacity.thresholds);
  }, [savedAreaCapacity]);

  const hasChanges =
    JSON.stringify(areas) !== JSON.stringify(savedAreas) ||
    JSON.stringify(thresholds) !== JSON.stringify(savedThresholds);

  const [draggingAreaId, setDraggingAreaId] = React.useState<string | null>(null);
  const [dropTargetAreaId, setDropTargetAreaId] = React.useState<string | null>(null);

  const [newName, setNewName] = React.useState("");
  const [newColor, setNewColor] = React.useState<AreaCapacityColor>("green");
  const [newType, setNewType] = React.useState<AreaCapacityType>("Area");
  const [newImageUrl, setNewImageUrl] = React.useState("");
  const [newCapacity, setNewCapacity] = React.useState("");

  const handleAdd = () => {
    const trimmedName = newName.trim();
    if (!trimmedName) return;

    const nextOrder = areas.reduce((max, area) => Math.max(max, area.order), -1) + 1;
    const capacity = normalizeCapacity(newCapacity === "" ? undefined : Number(newCapacity));
    const newArea: AreaCapacity = {
      id: crypto.randomUUID(),
      name: trimmedName,
      order: nextOrder,
      color: capacity !== undefined ? colorForCapacity(capacity, thresholds) : newColor,
      type: newType,
      imageUrl: normalizeImageUrl(newImageUrl),
      capacity,
    };

    setAreas((current) => [...current, newArea].sort((a, b) => a.order - b.order));
    setNewName("");
    setNewColor("green");
    setNewType("Area");
    setNewImageUrl("");
    setNewCapacity("");
  };

  const handleDelete = (areaId: string) => {
    confirmAlert({
      title: "Delete this Area?",
      message: "Are you sure? This can't be undone until you save.",
      buttons: [
        {
          label: "Yes",
          onClick: () => setAreas((current) => current.filter((area) => area.id !== areaId)),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const handleColorChange = (areaId: string, color: AreaCapacityColor) => {
    setAreas((current) =>
      current.map((area) => (area.id === areaId ? { ...area, color } : area))
    );
  };

  const handleTypeChange = (areaId: string, type: AreaCapacityType) => {
    setAreas((current) => current.map((area) => (area.id === areaId ? { ...area, type } : area)));
  };

  const handleImageUrlChange = (areaId: string, imageUrl: string) => {
    setAreas((current) =>
      current.map((area) =>
        area.id === areaId ? { ...area, imageUrl: normalizeImageUrl(imageUrl) } : area
      )
    );
  };

  const handleCapacityChange = (areaId: string, capacityInput: string) => {
    setAreas((current) =>
      current.map((area) => {
        if (area.id !== areaId) return area;
        const capacity = normalizeCapacity(
          capacityInput === "" ? undefined : Number(capacityInput)
        );
        return {
          ...area,
          capacity,
          color: capacity !== undefined ? colorForCapacity(capacity, thresholds) : area.color,
        };
      })
    );
  };

  const handleThresholdsChange = (_event: Event, value: number | number[]) => {
    if (!Array.isArray(value)) return;
    const [amber, red] = value;
    const next = { amber, red };

    setThresholds(next);
    setAreas((current) =>
      current.map((area) =>
        area.capacity !== undefined
          ? { ...area, color: colorForCapacity(area.capacity, next) }
          : area
      )
    );
  };

  const handleSave = async () => {
    const nextLastUpdated = new Date().toISOString();
    const settings: ConfigInputType[] = [
      {
        key: AREA_CAPACITY_KEY,
        value: JSON.stringify({
          areas: areas.map(({ id, name, order, color, type, imageUrl, capacity }) => ({
            id,
            name,
            order,
            color,
            type,
            imageUrl,
            capacity,
          })),
          thresholds,
          lastUpdated: nextLastUpdated,
        }),
      },
    ];

    try {
      await updateConfig({ variables: { settings } });
      setLastUpdated(nextLastUpdated);
      toast("Area capacity settings saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save areas");
    }
  };

  if (loading) return null;

  return (
    <PageContainer title="Area Capacity">
      <Box sx={{ mb: 3, maxWidth: 480 }}>
        <Box sx={{ color: "text.secondary", fontSize: 14, mb: 1.5 }}>
          Capacity color thresholds
        </Box>
        <Slider
          value={[thresholds.amber, thresholds.red]}
          onChange={handleThresholdsChange}
          min={0}
          max={100}
          step={1}
          disableSwap
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => `${value}%`}
          getAriaLabel={(index) => (index === 0 ? "Amber threshold" : "Red threshold")}
          sx={{
            height: 10,
            padding: "13px 0",
            "& .MuiSlider-rail": {
              opacity: 1,
              height: 10,
              borderRadius: 5,
              background: `linear-gradient(to right, ${swatchFor("green")} 0%, ${swatchFor("green")} ${thresholds.amber}%, ${swatchFor("amber")} ${thresholds.amber}%, ${swatchFor("amber")} ${thresholds.red}%, ${swatchFor("red")} ${thresholds.red}%, ${swatchFor("red")} 100%)`,
            },
            "& .MuiSlider-track": { display: "none" },
            "& .MuiSlider-thumb": {
              width: 18,
              height: 18,
              backgroundColor: "#fff",
              border: "2px solid #555",
              "&:hover, &.Mui-focusVisible": { boxShadow: "0 0 0 8px rgba(0,0,0,0.08)" },
              "&.Mui-active": { boxShadow: "0 0 0 10px rgba(0,0,0,0.1)" },
            },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", fontSize: 12, color: "text.secondary" }}>
            <ColorSwatch color="green" />
            Green 0–{thresholds.amber}%
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", fontSize: 12, color: "text.secondary" }}>
            <ColorSwatch color="amber" />
            Amber {thresholds.amber}–{thresholds.red}%
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", fontSize: 12, color: "text.secondary" }}>
            <ColorSwatch color="red" />
            Red {thresholds.red}–100%
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Name"
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="new-area-type-label">Type</InputLabel>
          <Select
            labelId="new-area-type-label"
            label="Type"
            value={newType}
            onChange={(event) => setNewType(event.target.value as AreaCapacityType)}
          >
            {TYPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }} disabled={newCapacity !== ""}>
          <InputLabel id="new-area-color-label">Color</InputLabel>
          <Select
            labelId="new-area-color-label"
            label="Color"
            value={newColor}
            onChange={(event) => setNewColor(event.target.value as AreaCapacityColor)}
          >
            {COLOR_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ColorSwatch color={option.value} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Image URL"
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={newImageUrl}
          onChange={(event) => setNewImageUrl(event.target.value)}
        />
        <TextField
          label="Capacity"
          variant="outlined"
          size="small"
          type="number"
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: 0, max: 100 }}
          value={newCapacity}
          onChange={(event) => setNewCapacity(event.target.value)}
          sx={{ width: 100 }}
        />
        <Button variant="contained" onClick={handleAdd} disabled={!newName.trim()}>
          Add area
        </Button>
      </Box>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Box component="span" sx={{ color: "text.secondary", fontSize: 14 }}>
            Drag rows to reorder areas, then save changes.
          </Box>
          <Box component="span" sx={{ color: "text.secondary", fontSize: 12 }}>
            Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleString() : "never"}
          </Box>
        </Box>
        <ButtonContainer>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </ButtonContainer>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: 80 }}>
                drag
              </TableCell>
              <TableCell align="left">name</TableCell>
              <TableCell align="left">order</TableCell>
              <TableCell align="left">type</TableCell>
              <TableCell align="left">color</TableCell>
              <TableCell align="left">image url</TableCell>
              <TableCell align="left">capacity</TableCell>
              <TableCell align="center">actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area) => (
              <TableRow
                key={area.id}
                draggable
                onDragStart={() => setDraggingAreaId(area.id)}
                onDragEnd={() => {
                  setDraggingAreaId(null);
                  setDropTargetAreaId(null);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (dropTargetAreaId !== area.id) {
                    setDropTargetAreaId(area.id);
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (!draggingAreaId) return;
                  setAreas((current) => reorderAreas(current, draggingAreaId, area.id));
                  setDraggingAreaId(null);
                  setDropTargetAreaId(null);
                }}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                style={{
                  opacity: draggingAreaId === area.id ? 0.55 : 1,
                  backgroundColor:
                    dropTargetAreaId === area.id ? "rgba(25, 118, 210, 0.08)" : undefined,
                  cursor: "move",
                }}
              >
                <TableCell align="left">::</TableCell>
                <TableCell align="left">{area.name}</TableCell>
                <TableCell align="left">{area.order}</TableCell>
                <TableCell align="left">
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={area.type}
                      onChange={(event) =>
                        handleTypeChange(area.id, event.target.value as AreaCapacityType)
                      }
                    >
                      {TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="left">
                  <FormControl
                    size="small"
                    sx={{ minWidth: 140 }}
                    disabled={area.capacity !== undefined}
                  >
                    <Select
                      value={area.color}
                      onChange={(event) =>
                        handleColorChange(area.id, event.target.value as AreaCapacityColor)
                      }
                    >
                      {COLOR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <ColorSwatch color={option.value} />
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="left">
                  <TextField
                    variant="outlined"
                    size="small"
                    sx={{ mb: "0 !important" }}
                    value={area.imageUrl ?? ""}
                    onChange={(event) => handleImageUrlChange(area.id, event.target.value)}
                  />
                </TableCell>
                <TableCell align="left">
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    value={area.capacity ?? ""}
                    onChange={(event) => handleCapacityChange(area.id, event.target.value)}
                    sx={{ width: 100, mb: "0 !important" }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    title="Delete"
                    variant="contained"
                    color="error"
                    onClick={() => handleDelete(area.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default Areas;
